import type { AppArea, AppAreaConfig } from '@/funnels/@types'
import type { FwfEntry } from '@/modules/Fwf/services/fwfResolver'
import { getAppAreaAtPath } from '@/funnels/funnels'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { loadFwfRegistry, resolveFwfTarget, selectFwfEntry } from '@/modules/Fwf/services/fwfResolver'
import { pickStickyVariant } from '@/modules/Landing/helpers/stickyVariant'

const FWF_ROUTER_EXPERIMENT = 'web_fwf_flow_router'

// pickStickyVariant's 24h default would drop the destination while the ABsmartly arm —
// keyed on the permanent deviceId — persists, moving a returning visitor to a second
// funnel id inside the same arm. Outlive the test instead.
const DESTINATION_TTL_MINUTES = 60 * 24 * 365

interface AbSmartly {
	assign: (name: string) => { variant: number; assigned: boolean }
	treatment: (name: string) => number
	variantConfig: (name: string) => Record<string, unknown>
}

interface Destination {
	/** Stable arm name — `pickStickyVariant` keys the sticky choice on it, and it is
	 *  what the caller reports to analytics so the arm is sliceable. */
	id: string
	/** Registry entry, bare so it drops into the `/fwf/:slug` route param. */
	slug: string
}

function bare(slug: string): string {
	return slug.trim().replace(/^\/+|\/+$/g, '')
}

/**
 * Dev/staging console only, deliberately not Sentry: every condition reported here is a
 * steady state re-evaluated on each pageview, so a paused destination would send one
 * event per visitor and swamp the quota. In production a dropped destination is visible
 * instead as zero `Experiment - FwF Router` events for that `fwfVariant`.
 */
function warn(message: string, extras?: Record<string, unknown>): void {
	if (!ENV_DEV_OR_STAGING) return

	console.warn(`[fwf-router] ${message}`, extras ?? '')
}

/**
 * Is this pageview an ad entry, i.e. the visitor standing on this appArea's own entry path?
 *
 * Read from the URL rather than `$route`, and deliberately: App mounts without awaiting
 * `router.isReady()`, so at boot `$route.name` is still undefined and a `name === 'fwf'`
 * guard passes for a direct `/fwf/<slug>/<page>` visit — which would then be hijacked to
 * another slug, losing `:page`. The pathname is authoritative from the first tick.
 *
 * It also keeps the router off every other surface. `initialPath` holds the appArea for a
 * week, so without this a treatment-arm visitor reloading on the paywall or mid-onboarding
 * would be thrown back to a pre-funnel's first screen while control keeps its position —
 * a straight bias in the headline metric, and a broken session for a real user.
 *
 * Matched against the domain's pathname map, not `landing.route.path`: the areas whose
 * landing route is declared in `router/index.js` carry `landing.route: null` (`main` among
 * them), and a `landing.route.path` test would silently never fire for them.
 */
export function isFwfRouterEntrySurface(
	parent: AppAreaConfig | null | undefined,
	hostname: string,
	path: string,
): boolean {
	if (!parent?.appArea) return false

	return getAppAreaAtPath(hostname, path) === parent.appArea
}

type FwfDestination = { kind: 'always'; appArea: string } | { kind: 'fork'; female: string; male: string }

/** The entry's destinations, in the two shapes the registry allows. */
function entryDestination(entry: FwfEntry): FwfDestination | null {
	const related = entry.relatedAppArea

	if (typeof related === 'string') {
		return related ? { kind: 'always', appArea: related } : null
	}
	if (!Array.isArray(related)) return null

	const always = related.find((target) => target?.always && typeof target.appArea === 'string')
	if (always) return { kind: 'always', appArea: always.appArea }

	const female = related.find((target) => target?.sex === 'female')?.appArea
	const male = related.find((target) => target?.sex === 'male')?.appArea
	if (typeof female === 'string' && typeof male === 'string') {
		return { kind: 'fork', female, male }
	}

	return null
}

/** The parent's own gender fork, or null for a single-gender funnel. */
function parentFork(parent: AppAreaConfig): { female: AppArea; male: AppArea } | null {
	const sex = parent.funnel?.redirectTo?.sex
	if (!sex?.female?.appArea || !sex?.male?.appArea) return null

	return { female: sex.female.appArea.appArea, male: sex.male.appArea.appArea }
}

/**
 * Why this pre-funnel is not comparable to the appArea under test, or null when it is.
 * A slug resolving anywhere else drops the treatment arm into a different funnel than
 * control, so the experiment measures nothing. Gender is a special case: a FwF forks
 * exactly as its parent does, so a matching fork map is the whole requirement.
 *
 * Without it the experiment's scope is the ABsmartly audience alone, and widening the
 * audience in the UI would widen the experiment with no review.
 */
function destinationMismatch(entry: FwfEntry, parent: AppAreaConfig): string | null {
	const destination = entryDestination(entry)
	if (!destination) return 'no resolvable destination'

	const fork = parentFork(parent)

	if (fork) {
		if (destination.kind !== 'fork') {
			return `parent forks by sex, entry always lands on ${destination.appArea}`
		}
		if (destination.female !== fork.female || destination.male !== fork.male) {
			return `fork mismatch: entry ${destination.female}/${destination.male}, parent ${fork.female}/${fork.male}`
		}
		return null
	}

	if (destination.kind !== 'always') {
		return 'parent does not fork, entry forks by sex'
	}
	if (destination.appArea !== parent.appArea) {
		return `entry lands on ${destination.appArea}, not ${parent.appArea}`
	}

	return null
}

/**
 * The destinations this build will actually serve, dropping the rest.
 *
 * Read before exposing the treatment arm, so a paused or staging-only pre-funnel takes
 * no traffic and no exposure: an arm that keeps enrolling while its visitors land on the
 * parent's landing is indistinguishable in the data from a genuine result.
 *
 * Dropping rather than stopping keeps the experiment running on what is left, which is
 * the trade the operator chose — the split silently changes mid-test, so every drop is
 * reported. `pickStickyVariant` re-rolls a visitor whose cached destination is gone.
 */
function servableDestinations(registry: FwfEntry[], parent: AppAreaConfig, destinations: Destination[]): Destination[] {
	const servable: Destination[] = []
	const dropped: string[] = []

	for (const destination of destinations) {
		const entry = selectFwfEntry(registry, destination.slug)

		// `isActive` covers unregistered, paused, and `preview` on a build that must not
		// serve previews. `resolveFwfTarget` matches `bootstrapFwf`'s own predicate, so an
		// active entry with a broken `relatedAppArea` cannot be exposed here and then
		// bounced to a landing downstream with nothing reported.
		const reason = !entry?.isActive
			? 'not servable'
			: !resolveFwfTarget(entry)
				? 'no resolvable target'
				: destinationMismatch(entry, parent)

		if (reason) dropped.push(`${destination.slug} (${reason})`)
		else servable.push(destination)
	}

	if (dropped.length) {
		warn('destinations dropped, split changed', { dropped, kept: servable.map((d) => d.id) })
	}

	return servable
}

/**
 * Destinations from the ABsmartly variant config, in the shape the landing experiments
 * already use: `{ variants: [{ id, … }] }`.
 *
 * A malformed or duplicate-id entry voids the whole list rather than being skipped: a
 * partly-read config would run an arm short of a destination, which reads in the data as
 * that destination converting to zero.
 */
function readDestinations(raw: unknown): Destination[] {
	const variants = raw && typeof raw === 'object' && 'variants' in raw ? raw.variants : null
	if (!Array.isArray(variants)) return []

	const destinations: Destination[] = []
	const ids = new Set<string>()

	for (const entry of variants) {
		if (!entry || typeof entry !== 'object') return []
		const id = typeof entry.id === 'string' ? entry.id.trim() : ''
		const slug = typeof entry.slug === 'string' ? bare(entry.slug) : ''
		if (!id || !slug || ids.has(id)) return []
		ids.add(id)
		destinations.push({ id, slug })
	}

	return destinations
}

/** `?fwf_router=a,b` — dev/staging stand-in for the variant config, one arm per slug. */
function readParam(raw: unknown): Destination[] {
	if (!ENV_DEV_OR_STAGING || typeof raw !== 'string') return []

	const variants = raw
		.split(',')
		.map(bare)
		.filter(Boolean)
		.map((slug) => ({ id: slug, slug }))

	return readDestinations({ variants })
}

type FwfRouterArm = 'none' | 'control' | 'treatment'

/**
 * Which arm this visitor is in, and — for control — their exposure.
 *
 * Synchronous and free: `assign()` computes the assignment without exposing, so a
 * visitor outside the audience costs nothing and a control-arm visitor costs one
 * exposure and no network. Only `'treatment'` goes on to need the registry.
 *
 * `assign()` also honours the kill-switch overrides `abSmartlyReady` already applied
 * (?safe, organic, Discovery, CRM autologin) and the ?exp= override.
 *
 * Deliberately NOT registered in `src/funnels/experiments/` — `getAppAreaExperiment`
 * returns on the first assigned experiment declaring a part, so a second `landing`-part
 * experiment on the same appArea would silently lose its exposure. Staying out of that
 * loop nests the two instead. Which appAreas take part is the ABsmartly audience's job
 * (the `app_area` attribute), so launching on another one needs no code.
 */
export function enrolInFwfRouter($absmartly: AbSmartly | null | undefined): FwfRouterArm {
	const assignment = $absmartly?.assign(FWF_ROUTER_EXPERIMENT)
	if (!assignment?.assigned) return 'none'

	if (assignment.variant === 0) {
		// Control must be exposed or there is nothing to compare the treatment against.
		$absmartly.treatment(FWF_ROUTER_EXPERIMENT)
		return 'control'
	}

	return 'treatment'
}

/**
 * The pre-funnel a treatment-arm visitor enters, or null to leave them where they are.
 *
 * Call only after `enrolInFwfRouter` returned `'treatment'`: this awaits the registry,
 * so the caller must cover the wait. Nobody outside the treatment arm pays for it — the
 * registry is fetched here and only here.
 *
 * The exposure comes last, once a destination is in hand: this arm is exposed if and
 * only if it is being redirected.
 */
export async function resolveFwfTreatmentDestination(
	parent: AppAreaConfig,
	$absmartly: AbSmartly,
	slugsParam?: unknown,
	variantParam?: unknown,
): Promise<Destination | null> {
	const mocked = readParam(slugsParam)
	const destinations = mocked.length ? mocked : readDestinations($absmartly.variantConfig(FWF_ROUTER_EXPERIMENT))

	if (!destinations.length) {
		warn('no destinations in the variant config')
		return null
	}

	const servable = servableDestinations(await loadFwfRegistry(), parent, destinations)

	if (!servable.length) {
		warn('nothing servable, no treatment')
		return null
	}

	// `?fwf_variant=<id>` pins the pick inside the servable list, which is the only way to
	// walk each destination on a stand — the draw is otherwise random-then-sticky.
	const pinned = ENV_DEV_OR_STAGING && typeof variantParam === 'string' ? variantParam.trim() : ''

	const destination = pinned
		? servable.find((candidate) => candidate.id === pinned)
		: pickStickyVariant({
				variants: servable,
				cacheKey: FWF_ROUTER_EXPERIMENT,
				ttlMinutes: DESTINATION_TTL_MINUTES,
			})

	if (!destination) {
		// Only reachable through the pin — the draw over a non-empty list always returns one.
		warn('pinned destination is not servable, no treatment', { pinned })
		return null
	}

	// Last, so the invariant is exact: this arm is exposed if and only if it is leaving.
	// Exposing before the pick let a pin at an unservable destination burn an exposure and
	// then leave the visitor on the parent funnel — invisible in the data, and the reason
	// the servability check exists at all.
	$absmartly.treatment(FWF_ROUTER_EXPERIMENT)

	return destination
}
